import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Facebook, ExternalLink, Clock, Menu, X, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentBrandIndex, setCurrentBrandIndex] = useState(0);

  // Brands data
  const brands = [
    {
      name: 'CIN',
      description: 'A CIN é líder de mercado em Portugal, oferecendo soluções completas para decoração e proteção. Com uma vasta gama de produtos de qualidade superior, está presente em projetos residenciais e comerciais.',
      website: 'https://cin.com/pt',
      logo: '/images/cin.jpg',
      specialties: ['Tintas Decorativas', 'Vernizes', 'Produtos Profissionais']
    },
    {
      name: 'Sotinco',
      description: 'A Sotinco é uma marca do grupo CIN, especialmente vocacionada para o sector da Construção Civil. Oferece uma gama completa de produtos destinados ao revestimento e isolamento de fachadas, paredes, tectos, madeira e metal.',
      website: 'https://sotinco.pt/',
      logo: '/images/logo_sotinco.jpg',
      specialties: ['Construção Civil', 'Fachadas', 'Impermeabilizantes']
    },
    {
      name: 'Nitin',
      description: 'A Nitin, marca do grupo CIN desde 2000, está direcionada para os profissionais de pintura. Oferece produtos perfeitamente adequados às necessidades profissionais com preços competitivos e qualidade garantida.',
      website: 'https://cin.com/nitin/',
      logo: '/images/nitin.jpg',
      specialties: ['Produtos Profissionais', 'Preços Competitivos', 'Qualidade CIN']
    }
  ];

  // Brand carousel navigation
  const nextBrand = () => {
    setCurrentBrandIndex((prev) => (prev + 1) % brands.length);
  };

  const prevBrand = () => {
    setCurrentBrandIndex((prev) => (prev - 1 + brands.length) % brands.length);
  };

  // Smooth scroll handler
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false); // Close mobile menu when navigating
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'sobre', 'lojas', 'marca', 'faq', 'dicas', 'contactos'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleTouchOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleScroll = () => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('touchstart', handleTouchOutside);
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobileMenuOpen]);

  const stores = [
    {
      name: 'Lousada',
      address: 'R. Lúcia Lousada 241, 4620-030 Lousada',
      phone: '255811610',
      phoneDisplay: '255 811 610',
      email: 'lojalousada@peixotoxavier.pt',
      schedule: [
        { days: 'Seg–Sex', hours: '09:00–12:30 | 14:30–19:00' },
        { days: 'Sáb', hours: '09:30–12:30' },
        { days: 'Dom', hours: 'Encerrado' }
      ],
      mapsUrl: 'https://www.google.com/maps/search/?api=1&query=R.+Lúcia+Lousada+241+4620-030+Lousada',
      photos: [
        { src: '/images/loja_lousada1.jpg', alt: 'Loja Lousada 1' },
        { src: '/images/loja_lousada2.jpg', alt: 'Loja Lousada 2' },
        { src: '/images/loja_lousada3.jpg', alt: 'Loja Lousada 3' },
        { src: '/images/loja_lousada4.jpg', alt: 'Loja Lousada 4' }
      ]
    },
    {
      name: 'Paredes',
      address: 'Av. Dr. Francisco Sá Carneiro 225',
      phone: '255784292',
      phoneDisplay: '255 784 292',
      schedule: [
        { days: 'Seg–Sex', hours: '09:00–12:30 | 14:30–19:00' },
        { days: 'Sáb', hours: '09:30–12:30' },
        { days: 'Dom', hours: 'Encerrado' }
      ],
      mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Av.+Dr.+Francisco+Sá+Carneiro+225+Paredes',
      photos: [
        { src: '/images/loja_paredes1.jpg', alt: 'Loja Paredes 1' },
        { src: '/images/loja_paredes2.jpg', alt: 'Loja Paredes 2' },
        { src: '/images/loja_paredes3.jpg', alt: 'Loja Paredes 3' }
      ]
    },
    {
      name: 'Penafiel',
      address: 'Rua do Porto, Edifício Alamela, 192, 4560-542 Penafiel',
      phone: '255720016',
      phoneDisplay: '255 720 016',
      schedule: [
        { days: 'Seg–Sex', hours: '09:00–12:30 | 14:30–19:00' },
        { days: 'Sáb', hours: '09:30–12:30' },
        { days: 'Dom', hours: 'Encerrado' }
      ],
      mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Rua+do+Porto+Edifício+Alamela+192+4560-542+Penafiel',
      photos: [
        { src: '/images/loja_penafiel1.jpg', alt: 'Loja Penafiel 1' },
        { src: '/images/loja_penafiel2.jpg', alt: 'Loja Penafiel 2' },
        { src: '/images/loja_penafiel3.jpg', alt: 'Loja Penafiel 3' },
        { src: '/images/loja_penafiel4.jpg', alt: 'Loja Penafiel 4' }
      ]
    },
    {
      name: 'Amarante',
      address: 'Rua da Estrada Real 399, 4560-245 Amarante',
      phone: '255431445',
      phoneDisplay: '255 431 445',
      schedule: [
        { days: 'Seg–Sex', hours: '09:00–12:30 | 14:30–19:00' },
        { days: 'Sáb', hours: '09:30–12:30' },
        { days: 'Dom', hours: 'Encerrado' }
      ],
      mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Rua+da+Estrada+Real+399+4560-245+Amarante',
      photos: [
        { src: '/images/loja_amarante3.jpg', alt: 'Loja Amarante 1' },
        { src: '/images/loja_amarante1.jpg', alt: 'Loja Amarante 2' },
        { src: '/images/loja_amarante2.jpg', alt: 'Loja Amarante 3' }
      ]
    }
  ];
  // Estado para o índice da foto de cada loja
  const [storePhotoIndexes, setStorePhotoIndexes] = useState({});

  const prevStorePhoto = (storeIdx, total) => {
    setStorePhotoIndexes(prev => ({
      ...prev,
      [storeIdx]: ((prev[storeIdx] || 0) - 1 + total) % total
    }));
  };

  const nextStorePhoto = (storeIdx, total) => {
    setStorePhotoIndexes(prev => ({
      ...prev,
      [storeIdx]: ((prev[storeIdx] || 0) + 1) % total
    }));
  };

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <img 
              src="/images/logo_pontodeCor.png" 
              alt="Ponto de Cor Logo" 
              className="h-10 md:h-12 lg:h-14 cursor-pointer"
              onClick={() => scrollToSection('hero')}
            />
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              {[
                { id: 'sobre', label: 'Sobre Nós' },
                { id: 'lojas', label: 'Lojas' },
                { id: 'marca', label: 'Marcas & Parcerias' },
                { id: 'faq', label: 'FAQ' },
                { id: 'dicas', label: 'Dicas' },
                { id: 'contactos', label: 'Contactos' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    activeSection === item.id ? 'text-primary' : 'text-gray-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-3 rounded-md hover:bg-gray-100 transition-colors touch-manipulation"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              onTouchStart={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-600" />
              ) : (
                <Menu className="h-6 w-6 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 py-4 border-t border-gray-200 bg-white/95 backdrop-blur-md">
              <div className="flex flex-col space-y-4">
                {[
                  { id: 'sobre', label: 'Sobre Nós' },
                  { id: 'lojas', label: 'Lojas' },
                  { id: 'marca', label: 'Marcas & Parcerias' },
                  { id: 'faq', label: 'FAQ' },
                  { id: 'dicas', label: 'Dicas' },
                  { id: 'contactos', label: 'Contactos' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    onTouchStart={() => scrollToSection(item.id)}
                    className={`text-left py-3 px-4 text-base font-medium transition-colors rounded-md hover:bg-gray-100 touch-manipulation ${
                      activeSection === item.id ? 'text-primary-red bg-gray-50' : 'text-gray-600'
                    }`}
                    style={{ minHeight: '44px' }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-24">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1622250544703-997d69d856e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxwYWludCUyMGNvbG9yc3xlbnwwfHx8fDE3NzIxMzQxMDB8MA&ixlib=rb-4.1.0&q=85" 
            alt="Cores e Tintas"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white pt-4">
          <div className="mb-10 md:mb-14 flex justify-center animate-fade-in">
            <img
              src="/images/logo_pontodeCor.png"
              alt="Ponto de Cor"
              className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl object-contain"
              style={{
                filter: 'drop-shadow(0 4px 24px rgba(255,255,255,0.15))'
              }}
            />
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 animate-fade-in leading-tight whitespace-nowrap">
            Peixoto & Xavier, Lda
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl mb-3 md:mb-4 text-gray-100 font-light">
            Soluções em Tintas e Vernizes CIN
          </p>
          <p className="text-base md:text-lg lg:text-xl mb-8 md:mb-12 text-gray-200 max-w-2xl mx-auto">
            Aqui encontra todas as cores e a solução ideal para o seu projeto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-primary-red hover:bg-primary-red/90 text-white px-8 py-6 text-lg"
              onClick={() => scrollToSection('lojas')}
            >
              <Phone className="mr-2 h-5 w-5" />
              Contactar loja mais próxima
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
              onClick={() => scrollToSection('lojas')}
            >
              <MapPin className="mr-2 h-5 w-5" />
              Ver lojas
            </Button>
          </div>
        </div>
      </section>

      {/* Sobre Nós Section */}
      <section id="sobre" className="section-sobre py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex flex-col items-center justify-center text-center">
            <h2 className="section-title text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Sobre Nós
            </h2>
            <div className="prose prose-lg text-gray-700 space-y-4 max-w-2xl">
              <p className="leading-relaxed text-justify md:text-center">
                A <strong>Peixoto & Xavier, Lda</strong> é uma referência no comércio de tintas e vernizes, 
                oferecendo soluções de qualidade para profissionais e particulares através das marcas 
                <strong className="text-primary-red"> CIN, Sotinco e Nitin</strong>.
              </p>
              <p className="leading-relaxed text-justify md:text-center">
                Com várias lojas na região, garantimos proximidade, aconselhamento especializado 
                e uma vasta gama de cores e produtos para dar vida aos seus projetos, desde decoração 
                residencial até grandes obras de construção civil.
              </p>
              <p className="leading-relaxed text-justify md:text-center">
                A nossa equipa dedicada está sempre pronta para o ajudar a encontrar a solução perfeita, 
                seja para pintura interior, exterior, projetos especializados ou obras profissionais.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* As Nossas Lojas Section */}
      <section id="lojas" className="section-lojas py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              As Nossas Lojas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Estamos presentes em <strong>Lousada, Paredes, Penafiel e Amarante</strong>,
com lojas especializadas em tintas e vernizes CIN.
            </p>
          </div>
          
          <div className="space-y-12">
            {stores.map((store, index) => (
              <Card key={index} className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className={`grid lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}> 
                  {/* Informações da Loja */}
                  <CardContent className={`p-8 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}> 
                    {/* Carrossel de Fotos real */}
                    <div className="w-full flex flex-col items-center mb-6">
                      <div className="relative w-full max-w-xs h-40 bg-gray-200 rounded-xl flex items-center justify-center overflow-hidden">
                        <img
                          src={store.photos[storePhotoIndexes[index] || 0]?.src}
                          alt={store.photos[storePhotoIndexes[index] || 0]?.alt}
                          className="w-full h-full object-cover"
                        />
                        {/* Setas de navegação */}
                        <button
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110"
                          aria-label="Foto anterior"
                          onClick={() => prevStorePhoto(index, store.photos.length)}
                        >
                          <ChevronLeft className="h-5 w-5 text-gray-700" />
                        </button>
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110"
                          aria-label="Próxima foto"
                          onClick={() => nextStorePhoto(index, store.photos.length)}
                        >
                          <ChevronRight className="h-5 w-5 text-gray-700" />
                        </button>
                      </div>
                      {/* Indicadores de fotos */}
                      <div className="flex justify-center mt-2 space-x-2">
                        {store.photos.map((_, photoIndex) => (
                          <button
                            key={photoIndex}
                            onClick={() => setStorePhotoIndexes(prev => ({ ...prev, [index]: photoIndex }))}
                            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                              photoIndex === (storePhotoIndexes[index] || 0) 
                                ? 'bg-primary-red scale-125' 
                                : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                            aria-label={`Ver foto ${photoIndex + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <MapPin className="h-6 w-6 mr-3 text-primary-red" />
                      Loja {store.name}
                    </h3>
                    <div className="space-y-6">
                      <div className="flex items-start">
                        <MapPin className="h-5 w-5 mr-3 mt-1 text-gray-500 flex-shrink-0" />
                        <p className="text-gray-700">{store.address}</p>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <Clock className="h-5 w-5 mr-2 text-gray-600" />
                          <span className="font-semibold text-gray-900">Horário de Funcionamento</span>
                        </div>
                        <div className="space-y-1.5 ml-7">
                          {store.schedule.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-gray-600 font-medium">{item.days}:</span>
                              <span className="text-gray-700">{item.hours}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        <Button 
                          className="w-full bg-primary-red hover:bg-primary-red/90 text-white"
                          onClick={() => window.location.href = `tel:${store.phone}`}
                        >
                          <Phone className="mr-2 h-5 w-5" />
                          Ligar: {store.phoneDisplay}
                        </Button>
                        
                        <Button 
                          className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white"
                          onClick={() => window.open(`https://wa.me/351${store.phone}`, '_blank')}
                        >
                          <MessageCircle className="mr-2 h-5 w-5" />
                          WhatsApp: {store.phoneDisplay}
                        </Button>
                        
                        {store.email && (
                          <Button 
                            variant="outline"
                            className="w-full border-2 border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white"
                            onClick={() => window.location.href = `mailto:${store.email}`}
                          >
                            <Mail className="mr-2 h-5 w-5" />
                            Enviar Email
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>

                  {/* Localização e Mapa */}
                  <div className={`bg-gray-100 ${index % 2 === 1 ? 'lg:col-start-1' : ''} flex items-center justify-center p-8`}>
                    <div className="text-center">
                      <div className="mb-6">
                        <MapPin className="h-16 w-16 text-primary-red mx-auto mb-4" />
                        <h4 className="text-xl font-bold text-gray-900 mb-2">Ver Localização</h4>
                        <p className="text-gray-600">{store.address}</p>
                      </div>
                      <Button 
                        size="lg" 
                        className="bg-primary-red hover:bg-primary-red/90 text-white"
                        onClick={() => window.open(store.mapsUrl, '_blank')}
                      >
                        <MapPin className="mr-2 h-5 w-5" />
                        Abrir no Google Maps
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Marcas & Parcerias Section */}
      <section id="marca" className="section-marca py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Marcas & Parcerias
            </h2>
            <p className="text-xl text-gray-600">
              Trabalhamos com as melhores marcas do <strong className="text-primary-red">Grupo CIN</strong>, 
              oferecendo soluções completas para todos os tipos de projetos
            </p>
          </div>

          {/* Brand Carousel */}
          <div className="relative max-w-4xl mx-auto">
            {/* Navigation Arrows */}
            <button
              onClick={prevBrand}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
              aria-label="Marca anterior"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>
            
            <button
              onClick={nextBrand}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
              aria-label="Próxima marca"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>

            {/* Brand Content */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0 items-center min-h-[500px]">
                <div className="bg-gray-50 h-full flex items-center justify-center p-8">
                  <div className="text-center">
                    <div className="w-56 h-40 bg-white rounded-2xl overflow-hidden shadow-lg mb-6 mx-auto flex items-center justify-center p-4">
                      <img 
                        src={brands[currentBrandIndex].logo} 
                        alt={`Logo ${brands[currentBrandIndex].name}`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-4">
                      {brands[currentBrandIndex].name}
                    </h4>
                    <div className="flex flex-wrap justify-center gap-2">
                      {brands[currentBrandIndex].specialties.map((specialty, index) => (
                        <div key={index} className="bg-primary-red/10 text-primary-red px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-8 md:p-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    {brands[currentBrandIndex].name}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">
                    {brands[currentBrandIndex].description}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      size="lg" 
                      className="bg-primary-red hover:bg-primary-red/90 text-white"
                      onClick={() => window.open(brands[currentBrandIndex].website, '_blank')}
                    >
                      <ExternalLink className="mr-2 h-5 w-5" />
                      Visitar Website
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Indicators */}
            <div className="flex justify-center mt-8 space-x-3">
              {brands.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBrandIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentBrandIndex 
                      ? 'bg-primary-red scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Ir para marca ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Perguntas Frequentes
            </h2>
            <p className="text-xl text-gray-600">
              Esclarecemos as suas dúvidas mais comuns sobre tintas e vernizes
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "Que quantidade de tinta preciso para pintar uma divisão?",
                answer: "Em média, 1 litro de tinta cobre aproximadamente 10-16m². Para calcular: (altura × perímetro da divisão) - área de portas e janelas ÷ 10. Recomendamos sempre comprar 10% extra para retoques futuros."
              },
              {
                question: "Qual a diferença entre tinta mate, acetinado, meio brilho e brilhante?",
                answer: "Mate: Não reflete luz, ideal para tetos e paredes com imperfeições. Acetinado: Ligeiramente brilhante, resistente e lavável, perfeita para salas e quartos. Meio brilho: Brilho moderado, ideal para áreas de tráfego médio. Brilhante: Muito resistente e lavável, ideal para cozinhas, casas de banho e portas."
              },
              {
                question: "Posso pintar diretamente sobre tinta antiga?",
                answer: "Depende do estado da tinta existente. Se estiver em bom estado, pode aplicar um primário adequado e depois a tinta nova. Se estiver a descascar ou com problemas, deve remover completamente antes de aplicar a nova tinta."
              },
              {
                question: "As tintas CIN são adequadas para exterior?",
                answer: "Sim, temos uma gama completa CIN para exteriores, incluindo tintas para fachadas, madeira e ferro. São formuladas para resistir às condições climatéricas portuguesas e oferecem proteção duradoura."
              },
              {
                question: "Oferecem serviço de mistura de cores personalizada?",
                answer: "Sim! Nas nossas lojas oferecemos um serviço de cor que permite criar qualquer cor do catálogo CIN ou reproduzir uma cor específica que nos apresente. Consulte-nos para mais detalhes."
              }
            ].map((item, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start">
                    <span className="text-primary-red mr-3 mt-1">Q:</span>
                    {item.question}
                  </h3>
                  <div className="ml-8">
                    <span className="text-primary-blue font-medium">R:</span>
                    <p className="text-gray-700 ml-3 inline">{item.answer}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Dicas Section */}
      <section id="dicas" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Dicas de Pintura
            </h2>
            <p className="text-xl text-gray-600">
              Conselhos profissionais para obter os melhores resultados
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Preparação da Superfície",
                tip: "Limpe bem as paredes, tape buracos e lixe superfícies irregulares. Uma boa preparação é 70% do resultado final.",
                icon: "🛠️"
              },
              {
                title: "Escolha do Pincel",
                tip: "Use pincéis de qualidade: cerdas naturais para tintas à base de solvente, sintéticas para tintas à base de água.",
                icon: "🖌️"
              },
              {
                title: "Ordem de Pintura",
                tip: "Pinte sempre: teto → paredes → rodapés. Comece pelos cantos e rematas, depois use o rolo nas áreas maiores.",
                icon: "📋"
              },
              {
                title: "Condições Ideais",
                tip: "Pinte entre 15-25°C com baixa humidade. Evite dias muito quentes, frios ou com vento para melhores resultados.",
                icon: "🌡️"
              },
              {
                title: "Técnica do Rolo",
                tip: "Use movimentos em 'W' ou 'M', depois passe o rolo na vertical para uniformizar. Não carregue demasiado tinta no rolo.",
                icon: "🏠"
              },
              {
                title: "Manutenção de Ferramentas",
                tip: "Deve utilizar os acessórios adequados para cada trabalho que vai executar.",
                icon: "🧽"
              },
              {
                title: "Proteção",
                tip: "Use fita de pintor de qualidade e plásticos para proteger móveis. Retire a fita ainda com tinta húmida.",
                icon: "🛡️"
              },
              {
                title: "Armazenamento",
                tip: "Guarde tintas abertas bem fechadas, em local fresco e seco. Coloque película plástica sobre a tinta antes de fechar.",
                icon: "📦"
              }
            ].map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300 group">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {item.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {item.tip}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Card className="inline-block">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-primary-red mb-2">
                  💡 Precisa de mais ajuda?
                </h3>
                <p className="text-gray-700 mb-4">
                  A nossa equipa especializada está sempre disponível para aconselhamento personalizado
                </p>
                <Button 
                  onClick={() => scrollToSection('contactos')}
                  className="bg-primary-red hover:bg-red-700 text-white"
                >
                  Contacte-nos
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contactos Section */}
      <section id="contactos" className="section-contactos py-24 bg-gradient-to-b from-white to-gray-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="section-title text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Contactos
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Estamos à sua disposição para o ajudar a encontrar a solução ideal para o seu projeto. 
            Entre em contacto connosco!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-primary-red hover:bg-primary-red/90 text-white px-8 py-6"
              onClick={() => scrollToSection('lojas')}
            >
              <Phone className="mr-2 h-5 w-5" />
              Ligar para uma loja
            </Button>
            
            <Button 
              size="lg" 
              className="bg-[#1877f2] hover:bg-[#1877f2]/90 text-white px-8 py-6"
              onClick={() => window.open('https://www.facebook.com/PeixotoXavierLda', '_blank')}
            >
              <Facebook className="mr-2 h-5 w-5" />
              Visitar Facebook
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8 text-center md:text-left">
            <div>
              <div className="flex justify-center md:justify-start">
                <img 
                  src="/images/logo_pontodeCor.png" 
                  alt="Ponto de Cor Logo" 
                  className="h-12 mb-4"
                />
              </div>
              <p className="text-gray-400 text-sm">
                Peixoto & Xavier, Lda — Soluções em Tintas e Vernizes CIN, Sotinco e Nitin
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Navegação</h3>
              <div className="space-y-2">
                {[
                  { id: 'sobre', label: 'Sobre Nós' },
                  { id: 'lojas', label: 'As Nossas Lojas' },
                  { id: 'marca', label: 'Marcas & Parcerias' },
                  { id: 'faq', label: 'FAQ' },
                  { id: 'contactos', label: 'Contactos' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block text-gray-400 hover:text-white transition-colors text-sm mx-auto md:mx-0"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Siga-nos</h3>
              <div className="flex justify-center md:justify-start">
                <a
                  href="https://www.facebook.com/PeixotoXavierLda"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-400 hover:text-white transition-colors"
                >
                  <Facebook className="h-5 w-5 mr-2" />
                  Facebook
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Peixoto & Xavier, Lda. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;